import { useEffect, useState } from "react";
import { cachedGet } from "../api";
import { accountLinks, brandName, exploreLinks } from "../content";
import { useSession } from "../session";
import Logo from "./Logo";

function pageHref(path) {
  if (!path || path === "/") return "#home";
  if (path.startsWith("#")) return path;
  return path.startsWith("/") ? `#${path}` : `#/${path}`;
}

function isGuestLink(href) {
  return ["#/login", "#/signup", "#/register"].includes(href);
}

const pageTabs = [
  { label: "Travel Reward", href: "#trip" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Referrals", href: "#referrals" },
  { label: "Income", href: "#income" },
];

function withPageTabs(items) {
  const next = items.map((item) =>
    item.label.trim().toLowerCase() === "packages" ? { ...item, label: "Travel Reward", href: "#trip" } : item
  );
  const have = new Set(next.map((item) => item.href));
  const missing = pageTabs.filter((tab) => !have.has(tab.href));
  if (!missing.length) return next;
  const about = next.findIndex((item) => item.href === "#about");
  const at = about >= 0 ? about + 1 : next.length ? 1 : 0;
  next.splice(at, 0, ...missing);
  return next;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [nav, setNav] = useState(exploreLinks);
  const { token } = useSession();
  const links = withPageTabs(token ? nav.filter((link) => !isGuestLink(link.href)) : nav)
    // Contact tab hidden from the header for now (footer still links to it). Delete this line to bring it back.
    .filter((link) => !["#contact", "#/contact"].includes(link.href));

  useEffect(() => {
    cachedGet("/api/v1/public/site")
      .then((result) => {
        const items = result.data?.navigation;
        if (Array.isArray(items) && items.length) {
          setNav(items.map((item) => ({ label: item.label, href: pageHref(item.path) })));
        }
        document.title = brandName(result.data?.siteName);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("nav-open", open);
    return () => document.body.classList.remove("nav-open");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className={scrolled ? "header is-scrolled" : "header"}>
      <Logo />

      <nav className="nav-desktop" aria-label="Main">
        {links.map((link) => (
          <a key={link.href} href={link.href}>
            {link.label}
          </a>
        ))}
      </nav>

      <div className="header-actions">
        {token ? (
          <a className="btn btn-primary desktop-only" href="#/dashboard">
            Dashboard
          </a>
        ) : (
          <>
            <a className="btn btn-ghost desktop-only" href={accountLinks[0].href}>
              {accountLinks[0].label}
            </a>
            <a className="btn btn-primary desktop-only" href={accountLinks[1].href}>
              {accountLinks[1].label}
            </a>
          </>
        )}
        <button
          className="menu-btn"
          type="button"
          aria-label={open ? "Close menu" : "Menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((value) => !value)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      </div>

      {open && (
        <>
          <button className="backdrop" type="button" aria-label="Close menu" onClick={() => setOpen(false)} />
          <nav id="mobile-menu" className="drawer" aria-label="Mobile">
            {links.map((link) => (
              <a key={link.href} href={link.href} onClick={() => setOpen(false)}>
                {link.label}
              </a>
            ))}
            {token ? (
              <a href="#/dashboard" onClick={() => setOpen(false)}>
                Dashboard
              </a>
            ) : (
              accountLinks.map((link) => (
                <a key={link.label} href={link.href} onClick={() => setOpen(false)}>
                  {link.label}
                </a>
              ))
            )}
          </nav>
        </>
      )}
    </header>
  );
}
